import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Select,
  OutlinedInput,
  Chip,
} from "@mui/material";
import BASE_URL from "../config/appconfig";
import { Checkbox, FormControlLabel, MenuItem } from "@mui/material";

const churchAgeOptions = [...Array(51).keys(), "50+"];

const AddContactDialog = ({
  open,
  handleClose,
  refreshContacts,
  selectedContact,
  mode,
  fieldConfigs = [],
  showSnackbar,
}) => {
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    church_name: "",
    church_age: "",
    city: "",
    country: "",
    is_admin: false,
  });
  const [customData, setCustomData] = useState({});

  const isEdit = Boolean(selectedContact);
  const isPreview = mode === "preview";
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (mode === "edit" || mode === "preview") {
      if (selectedContact) {
        const clean = (val) => (!val || val === "-" ? "" : val);
        setFormData({
          full_name: selectedContact.full_name || "",
          email: selectedContact.email || "",
          church_name: clean(selectedContact.church_name),
          church_age: clean(selectedContact.church_age),
          city: clean(selectedContact.city),
          country: clean(selectedContact.country),
          is_admin: selectedContact.is_admin || false,
        });
        setCustomData(selectedContact.custom_data || {});
      }
    } else if (mode === "add") {
      setFormData({
        full_name: "",
        email: "",
        church_name: "",
        church_age: "",
        city: "",
        country: "",
        is_admin: false,
      });
      setCustomData({});
    }
  }, [selectedContact, mode]);

  const validate = () => {
    let newErrors = {};
    if (!formData.email || !formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)
    ) {
      newErrors.email = "Invalid email address";
    }

    fieldConfigs.forEach((fc) => {
      if (fc.is_required) {
        const val = customData[fc.field_key];
        let empty;
        if (fc.field_type === "checkbox") {
          empty = val === undefined || val === null;
        } else if (Array.isArray(val)) {
          empty = val.length === 0;
        } else {
          empty = val === undefined || val === null || String(val).trim() === "";
        }
        if (empty) newErrors[fc.field_key] = `${fc.field_label} is required`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isFormValid =
    formData.email?.trim() &&
    /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;
    setFormData((prev) => ({ ...prev, [name]: newValue }));

    if (name === "email" && type !== "checkbox") {
      setErrors((prev) => {
        let errorMessage = "";
        if (!newValue.trim()) {
          errorMessage = "This field is required";
        } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(newValue)) {
          errorMessage = "Invalid email address";
        }
        return { ...prev, email: errorMessage };
      });
    }
  };

  const handleCustomChange = (fieldKey, value) => {
    setCustomData((prev) => ({ ...prev, [fieldKey]: value }));
    setErrors((prev) => ({ ...prev, [fieldKey]: "" }));
  };

  const handleEmailBlur = async () => {
    if (
      mode === "add" &&
      /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)
    ) {
      try {
        const res = await fetch(
          `${BASE_URL}/api/contact-list/check-email?email=${encodeURIComponent(
            formData.email
          )}`
        );
        const result = await res.json();
        if (result.exists) {
          setErrors((prev) => ({
            ...prev,
            email: "This email is already in the contact list.",
          }));
        }
      } catch {
        // silently ignore network errors
      }
    }
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    try {
      let url;
      let method;
      let payload;

      if (isPreview) {
        url = `${BASE_URL}/api/people/update/${selectedContact._id}`;
        method = "PUT";
        payload = { is_admin: formData.is_admin };
      } else if (isEdit) {
        url = `${BASE_URL}/api/contact-list/update/${selectedContact._id}`;
        method = "PUT";
        payload = {
          ...formData,
          full_name: formData.full_name?.trim() || null,
          church_name: formData.church_name?.trim() || null,
          church_age: formData.church_age?.trim() || null,
          city: formData.city?.trim() || null,
          country: formData.country?.trim() || null,
          custom_data: customData,
        };
      } else {
        url = `${BASE_URL}/api/contact-list`;
        method = "POST";
        payload = {
          ...formData,
          full_name: formData.full_name?.trim() || null,
          church_name: formData.church_name?.trim() || null,
          church_age: formData.church_age?.trim() || null,
          city: formData.city?.trim() || null,
          country: formData.country?.trim() || null,
          custom_data: customData,
        };
      }

      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (result.success) {
        showSnackbar(
          isPreview
            ? "Admin access updated successfully"
            : isEdit
            ? "Contact updated successfully"
            : "Contact added successfully",
          "success"
        );
        refreshContacts();
        handleClose();
      } else {
        showSnackbar(result.message || "Something went wrong", "error");
      }
    } catch (error) {
      showSnackbar("Server error. Please try again.", "error");
      console.error(error);
    }
  };

  const renderCustomField = (fc) => {
    if (isPreview) {
      const val = customData[fc.field_key];
      const display = Array.isArray(val)
        ? val.join(", ") || "-"
        : fc.field_type === "checkbox"
        ? val === true || val === "true" ? "Yes" : "No"
        : val || "-";
      return <Typography className="preview-value">{display}</Typography>;
    }

    if (fc.field_type === "multi-select") {
      const selected = Array.isArray(customData[fc.field_key])
        ? customData[fc.field_key]
        : [];
      return (
        <Select
          multiple
          fullWidth
          size="small"
          value={selected}
          onChange={(e) => handleCustomChange(fc.field_key, e.target.value)}
          input={<OutlinedInput size="small" />}
          renderValue={(sel) => (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {sel.map((v) => (
                <Chip key={v} label={v} size="small" sx={{ fontSize: 11 }} />
              ))}
            </Box>
          )}
          displayEmpty
          error={!!errors[fc.field_key]}
          sx={{ fontSize: 13 }}
        >
          {(fc.options || []).map((opt) => (
            <MenuItem key={opt} value={opt} sx={{ fontSize: 13 }}>
              {opt}
            </MenuItem>
          ))}
        </Select>
      );
    }

    if (fc.field_type === "select") {
      return (
        <TextField
          select
          fullWidth
          size="small"
          value={customData[fc.field_key] || ""}
          onChange={(e) => handleCustomChange(fc.field_key, e.target.value)}
          error={!!errors[fc.field_key]}
          helperText={errors[fc.field_key]}
          slotProps={{ select: { displayEmpty: true } }}
        >
          <MenuItem value="">Select...</MenuItem>
          {(fc.options || []).map((opt) => (
            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
          ))}
        </TextField>
      );
    }

    if (fc.field_type === "textarea") {
      return (
        <TextField
          fullWidth
          size="small"
          multiline
          rows={3}
          value={customData[fc.field_key] || ""}
          onChange={(e) => handleCustomChange(fc.field_key, e.target.value)}
          error={!!errors[fc.field_key]}
          helperText={errors[fc.field_key]}
          placeholder={`Enter ${fc.field_label.toLowerCase()}`}
          sx={{ "& .MuiInputBase-input::placeholder": { fontSize: "12px", opacity: 0.5 } }}
        />
      );
    }

    if (fc.field_type === "number") {
      return (
        <TextField
          fullWidth
          size="small"
          type="number"
          value={customData[fc.field_key] || ""}
          onChange={(e) => handleCustomChange(fc.field_key, e.target.value)}
          error={!!errors[fc.field_key]}
          helperText={errors[fc.field_key]}
          placeholder={`Enter ${fc.field_label.toLowerCase()}`}
          sx={{ "& .MuiInputBase-input::placeholder": { fontSize: "12px", opacity: 0.5 } }}
        />
      );
    }

    if (fc.field_type === "date") {
      return (
        <TextField
          fullWidth
          size="small"
          type="date"
          value={customData[fc.field_key] || ""}
          onChange={(e) => handleCustomChange(fc.field_key, e.target.value)}
          error={!!errors[fc.field_key]}
          helperText={errors[fc.field_key]}
          slotProps={{ inputLabel: { shrink: true } }}
        />
      );
    }

    if (fc.field_type === "checkbox") {
      const val = customData[fc.field_key];
      return (
        <Box sx={{ display: "flex", gap: 2, mt: 0.5 }}>
          {["Yes", "No"].map((option) => {
            const isSelected =
              option === "Yes"
                ? val === true || val === "true"
                : val === false || val === "false";
            return (
              <FormControlLabel
                key={option}
                control={
                  <Checkbox
                    checked={isSelected}
                    onChange={() =>
                      handleCustomChange(fc.field_key, option === "Yes")
                    }
                    size="small"
                  />
                }
                label={<Typography sx={{ fontSize: 13 }}>{option}</Typography>}
              />
            );
          })}
        </Box>
      );
    }

    return (
      <TextField
        fullWidth
        size="small"
        value={customData[fc.field_key] || ""}
        onChange={(e) => handleCustomChange(fc.field_key, e.target.value)}
        error={!!errors[fc.field_key]}
        helperText={errors[fc.field_key]}
        placeholder={`Enter ${fc.field_label.toLowerCase()}`}
        sx={{ "& .MuiInputBase-input::placeholder": { fontSize: "12px", opacity: 0.5 } }}
      />
    );
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: { className: "editor-dialog-paper" },
        }}
      >
        <DialogContent className="editor-dialog-content">
          <Typography className="editor-dialog-title">
            {isPreview
              ? "View Preview"
              : isEdit
              ? "Edit Contact"
              : "Add Contact"}
          </Typography>

          <Grid container spacing={2}>
            <Grid item size={{ xs: 12, md: 6 }}>
              <Typography className="editor-dialog-label">
                Full Name
              </Typography>
              {isPreview ? (
                <Typography className="preview-value">
                  {formData.full_name || "-"}
                </Typography>
              ) : (
                <TextField
                  name="full_name"
                  fullWidth
                  size="small"
                  value={formData.full_name}
                  onChange={handleChange}
                  placeholder="Enter the full name"
                  sx={{
                    "& .MuiInputBase-input::placeholder": {
                      fontSize: "12px",
                      opacity: 0.5,
                    },
                  }}
                />
              )}
            </Grid>

            <Grid item size={{ xs: 12, md: 6 }}>
              <Typography className="editor-dialog-label">
                Email Address <span style={{ color: "red" }}>*</span>
              </Typography>
              {isPreview ? (
                <Typography className="preview-value">
                  {formData.email || "-"}
                </Typography>
              ) : (
                <TextField
                  name="email"
                  fullWidth
                  size="small"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={handleEmailBlur}
                  placeholder="Enter the email address"
                  error={!!errors.email}
                  helperText={errors.email}
                  sx={{
                    "& .MuiInputBase-input::placeholder": {
                      fontSize: "12px",
                      opacity: 0.5,
                    },
                  }}
                />
              )}
            </Grid>

            <Grid item size={{ xs: 12, md: 6 }}>
              <Typography className="editor-dialog-label">City</Typography>
              {isPreview ? (
                <Typography
                  className="preview-value"
                  sx={!formData.city ? { textAlign: "center" } : {}}
                >
                  {formData.city || "-"}
                </Typography>
              ) : (
                <TextField
                  name="city"
                  fullWidth
                  size="small"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder="Enter the city"
                  sx={{
                    "& .MuiInputBase-input::placeholder": {
                      fontSize: "12px",
                      opacity: 0.5,
                    },
                  }}
                />
              )}
            </Grid>

            <Grid item size={{ xs: 12, md: 6 }}>
              <Typography className="editor-dialog-label">Country</Typography>
              {isPreview ? (
                <Typography
                  className="preview-value"
                  sx={!formData.country ? { textAlign: "center" } : {}}
                >
                  {formData.country || "-"}
                </Typography>
              ) : (
                <TextField
                  name="country"
                  fullWidth
                  size="small"
                  value={formData.country}
                  onChange={handleChange}
                  placeholder="Enter the country"
                  sx={{
                    "& .MuiInputBase-input::placeholder": {
                      fontSize: "12px",
                      opacity: 0.5,
                    },
                  }}
                />
              )}
            </Grid>

            <Grid item size={{ xs: 12, md: 6 }}>
              <Typography className="editor-dialog-label">
                Name of the Church
              </Typography>
              {isPreview ? (
                <Typography
                  className="preview-value"
                  sx={!formData.church_name ? { textAlign: "center" } : {}}
                >
                  {formData.church_name || "-"}
                </Typography>
              ) : (
                <TextField
                  name="church_name"
                  fullWidth
                  size="small"
                  value={formData.church_name}
                  onChange={handleChange}
                  placeholder="Enter the name of the church"
                  sx={{
                    "& .MuiInputBase-input::placeholder": {
                      fontSize: "12px",
                      opacity: 0.5,
                    },
                  }}
                />
              )}
            </Grid>

            <Grid item size={{ xs: 12, md: 6 }}>
              <Typography className="editor-dialog-label">
                Age of the Church
              </Typography>
              {isPreview ? (
                <Typography
                  className="preview-value"
                  sx={!formData.church_age ? { textAlign: "center" } : {}}
                >
                  {formData.church_age || "-"}
                </Typography>
              ) : (
                <TextField
                  select
                  name="church_age"
                  fullWidth
                  size="small"
                  value={formData.church_age}
                  onChange={handleChange}
                  sx={{
                    "& .MuiSelect-select": {
                      color: formData.church_age ? "#000" : "#9e9e9e",
                    },
                  }}
                  slotProps={{
                    select: {
                      displayEmpty: true,
                      MenuProps: {
                        PaperProps: { style: { maxHeight: 200 } },
                      },
                    },
                  }}
                >
                  <MenuItem value="">Choose church age</MenuItem>
                  {churchAgeOptions.map((age) => (
                    <MenuItem key={age} value={String(age)}>
                      {age}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </Grid>

            {/* Custom Fields */}
            {fieldConfigs.map((fc) => (
              <Grid item size={{ xs: 12, md: 6 }} key={fc._id}>
                <Typography className="editor-dialog-label">
                  {fc.field_label}{" "}
                  {fc.is_required && !isPreview && (
                    <span style={{ color: "red" }}>*</span>
                  )}
                </Typography>
                {renderCustomField(fc)}
                {errors[fc.field_key] && (
                  <Typography sx={{ fontSize: 11, color: "#d32f2f", mt: 0.5 }}>
                    {errors[fc.field_key]}
                  </Typography>
                )}
              </Grid>
            ))}

            {(isEdit || isPreview) && (
              <Grid item xs={12}>
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: "#009CCC",
                    mt: 2,
                    fontWeight: 500,
                  }}
                >
                  Note: You can give admin access to this user.
                </Typography>

                <FormControlLabel
                  control={
                    <Checkbox
                      name="is_admin"
                      checked={!!formData.is_admin}
                      onChange={handleChange}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: "15px", fontWeight: 500 }}>
                      Admin Access
                    </Typography>
                  }
                />
              </Grid>
            )}
          </Grid>

          <Box display="flex" justifyContent="flex-end" gap={2} mt={4}>
            <Button onClick={handleClose} color="inherit">
              CANCEL
            </Button>

            <Button
              variant="contained"
              disabled={!isFormValid}
              sx={{
                backgroundColor: "#ef2d2d",
                "&:hover": { backgroundColor: "#d91c1c" },
              }}
              onClick={handleSubmit}
            >
              {isPreview ? "SAVE ACCESS" : isEdit ? "UPDATE" : "SUBMIT"}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AddContactDialog;
